import type {
  WaySightAIWasm,
  ForecastConfig,
  ForecastResult,
  DataStats,
  ACFPACFResult,
  StationarityTestResults,
} from '../../types/wasm';

/**
 * WasmEngine - Wrapper class for loading and interacting with the WaySightAI WASM module
 */
export class WasmEngine {
  private wasmModule: WaySightAIWasm | null = null;
  private isInitialized = false;
  private loadingPromise: Promise<void> | null = null;

  /**
   * Load the WASM module
   */
  async load(): Promise<void> {
    // Return existing loading promise if already loading
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Return immediately if already loaded
    if (this.isInitialized && this.wasmModule) {
      return Promise.resolve();
    }

    this.loadingPromise = this.initializeWasm();
    await this.loadingPromise;
    this.loadingPromise = null;
  }

  private async initializeWasm(): Promise<void> {
    try {
      // Dynamically import the WASM module from the monorepo root pkg directory
      // The WASM package is generated at build time by wasm-pack, so TypeScript
      // cannot verify its existence during compilation. We use @ts-expect-error here
      // because the import is validated at runtime with proper error handling.
      // @ts-expect-error: Dynamic WASM import resolved at runtime
      const wasmModule = await import(/* @vite-ignore */ '/pkg/waysightai_wasm.js');

      // Initialize the WASM module
      await wasmModule.default();
      wasmModule.init();

      this.wasmModule = wasmModule as unknown as WaySightAIWasm;
      this.isInitialized = true;

      console.log('[WasmEngine] WASM module loaded successfully');
    } catch (error) {
      console.error('[WasmEngine] Failed to load WASM module:', error);
      throw new Error(
        `Failed to load WASM module: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Ensure WASM is loaded before calling methods
   */
  private ensureInitialized(): WaySightAIWasm {
    if (!this.isInitialized || !this.wasmModule) {
      throw new Error('WASM module not initialized. Call load() first.');
    }
    return this.wasmModule;
  }

  /**
   * Convert JavaScript array to Float64Array for WASM
   */
  private toFloat64Array(data: number[]): Float64Array {
    return new Float64Array(data);
  }

  /**
   * Load and validate time series data
   */
  async loadAndValidate(
    timestamps: number[],
    values: number[],
    isUnixMs: boolean
  ): Promise<DataStats> {
    const wasm = this.ensureInitialized();
    const resultJson = wasm.load_and_validate(timestamps, values, isUnixMs);
    return JSON.parse(resultJson);
  }

  /**
   * Get data statistics
   */
  async getDataStats(
    timestamps: number[],
    values: number[],
    isUnixMs: boolean
  ): Promise<DataStats> {
    const wasm = this.ensureInitialized();
    const resultJson = wasm.get_data_stats(timestamps, values, isUnixMs);
    return JSON.parse(resultJson);
  }

  /**
   * Run forecast with given configuration
   */
  async runForecast(
    timestamps: number[],
    values: number[],
    isUnixMs: boolean,
    config: ForecastConfig
  ): Promise<ForecastResult> {
    const wasm = this.ensureInitialized();

    console.log('[WasmEngine] runForecast called with config:', {
      horizon: config.horizon,
      model_type: config.model_type,
      confidence_level: config.confidence_level
    });

    // DEBUG: Check if horizon is valid
    if (!config.horizon || config.horizon <= 0) {
      console.error('[WasmEngine] BUG: Invalid horizon detected!', {
        horizon: config.horizon,
        config_snapshot: JSON.parse(JSON.stringify(config))
      });
    }

    const configJson = JSON.stringify(config);
    console.log('[WasmEngine] Serialized config JSON:', configJson);

    const resultJson = wasm.run_forecast(timestamps, values, isUnixMs, configJson);
    const result = JSON.parse(resultJson);

    console.log('[WasmEngine] Forecast completed:', {
      forecasts_count: result.forecasts?.length,
      expected_horizon: config.horizon
    });

    return result;
  }

  /**
   * Calculate ACF and PACF for lag analysis
   */
  async calculateACFPACF(values: number[], maxLag?: number): Promise<ACFPACFResult> {
    const wasm = this.ensureInitialized();
    const resultJson = wasm.calculate_acf_pacf(values, maxLag);
    return JSON.parse(resultJson);
  }

  /**
   * Run stationarity tests (ADF and KPSS)
   */
  async runStationarityTests(values: number[]): Promise<StationarityTestResults> {
    const wasm = this.ensureInitialized();
    const resultJson = wasm.run_stationarity_tests(values);
    return JSON.parse(resultJson);
  }

  /**
   * Apply preprocessing to time series data
   */
  async preprocessData(
    timestamps: number[],
    values: number[],
    isUnixMs: boolean,
    config: {
      missing_value_method?: string;
      handle_outliers?: boolean;
      outlier_method?: string;
      iqr_multiplier?: number;
      transformation?: string;
      boxcox_lambda?: number | null;
      differencing_order?: number;
    }
  ): Promise<{
    timestamps: number[];
    values: number[];
    stats: any;
    applied_config: any;
  }> {
    const wasm = this.ensureInitialized();

    // Default config
    const preprocessConfig = {
      missing_value_method: config.missing_value_method || 'none',
      handle_outliers: config.handle_outliers || false,
      outlier_method: config.outlier_method || 'iqr',
      iqr_multiplier: config.iqr_multiplier || 1.5,
      transformation: config.transformation || 'none',
      boxcox_lambda: config.boxcox_lambda,
      differencing_order: config.differencing_order || 0,
    };

    const configJson = JSON.stringify(preprocessConfig);
    const resultJson = wasm.preprocess_data(timestamps, values, isUnixMs, configJson);
    return JSON.parse(resultJson);
  }

  /**
   * Detect outliers using IQR method
   */
  async detectOutliersIQR(values: number[]): Promise<{ indices: number[]; z_scores: number[] }> {
    const wasm = this.ensureInitialized();
    const result = wasm.detect_outliers_iqr(values);
    return result;
  }

  /**
   * Impute missing values with mean
   */
  async imputeMean(values: number[]): Promise<{
    cleaned_values: number[];
    changes_made: string[];
    changed_indices: number[];
  }> {
    const wasm = this.ensureInitialized();
    const result = wasm.impute_mean(values);
    return result;
  }

  /**
   * Impute missing values with median
   */
  async imputeMedian(values: number[]): Promise<{
    cleaned_values: number[];
    changes_made: string[];
    changed_indices: number[];
  }> {
    const wasm = this.ensureInitialized();
    const result = wasm.impute_median(values);
    return result;
  }

  /**
   * Impute missing values with linear interpolation
   */
  async imputeLinearInterpolation(values: number[]): Promise<{
    cleaned_values: number[];
    changes_made: string[];
    changed_indices: number[];
  }> {
    const wasm = this.ensureInitialized();
    const result = wasm.impute_linear_interpolation(values);
    return result;
  }

  /**
   * Remove outliers (replace with NaN)
   */
  async removeOutliers(values: number[]): Promise<{
    cleaned_values: number[];
    changes_made: string[];
    changed_indices: number[];
  }> {
    const wasm = this.ensureInitialized();
    const result = wasm.remove_outliers(values);
    return result;
  }

  /**
   * Cap outliers at percentiles
   */
  async capOutliers(
    values: number[],
    percentile: number
  ): Promise<{
    cleaned_values: number[];
    changes_made: string[];
    changed_indices: number[];
  }> {
    const wasm = this.ensureInitialized();
    const result = wasm.cap_outliers(values, percentile);
    return result;
  }

  /**
   * Check if WASM module is loaded
   */
  isLoaded(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
export const wasmEngine = new WasmEngine();
